import React, { useCallback, useEffect, useRef, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import BoltIcon from '@mui/icons-material/Bolt';
import CloseButton from '../Common/CloseButton';
import NodeEditorStatusAlert from '../Common/NodeEditorStatusAlert';
import ConfigurationErrorSummary, {
  CONFIGURATION_ERROR_BACKGROUND
} from '../Common/ConfigurationErrorSummary';
// Import the specific setting components that will be rendered inside the dialog
import ActionsCreateFile from './ActionsCreateFile';
import ActionsAddCsvColumn from './ActionsAddCsvColumn';
import ActionsAddExcelColumn from './ActionsAddExcelColumn';
import ActionsAddExcelRow from './ActionsAddExcelRow';
import ActionsCopyFile from './ActionsCopyFile';
import ActionsCreateFolder from './ActionsCreateFolder';
import ActionsCreateProject from './ActionsCreateProject';
import ActionsGenerateWidget from './ActionsGenerateWidget'; // Import ActionsGenerateWidget
import ActionsCreateDocx from './ActionsCreateDocx';
import ActionsCreateDocxTemplate from './ActionsCreateDocxTemplate';
import ActionsCreateDocxTemplateV2 from './ActionsCreateDocxTemplateV2';
import ActionsReplaceWordText from './ActionsReplaceWordText';
import ActionsAnonymizePII from './ActionsAnonymizePII';
import ActionsCreateExcelFromTemplate from './ActionsCreateExcelFromTemplate';
import ActionsAddPasswordDocx from './ActionsAddPasswordDocx';
import ActionsAddPasswordExcel from './ActionsAddPasswordExcel';
import ActionsConvertDocxToPdf from './ActionsConvertDocxToPdf';
import ActionsConvertPdfToDocx from './ActionsConvertPdfToDocx';
import ActionsPdfTextToWord from './ActionsPdfTextToWord';
import ActionsCreatePdf from './ActionsCreatePdf';
import ActionsInsertImageIntoPdf from './ActionsInsertImageIntoPdf';
import ActionsSearchAndInsertImageIntoPdf from './ActionsSearchAndInsertImageIntoPdf';
import ActionsSearchAndInsertRichTextIntoPdf from './ActionsSearchAndInsertRichTextIntoPdf';
import ActionsCreateZipFile from './ActionsCreateZipFile';
import ActionsCreateJsonFile from './ActionsCreateJsonFile';
import ActionsCreateXmlFile from './ActionsCreateXmlFile';
import ActionsSendAPI from './ActionsSendAPI';
import ActionsSendEmail from './ActionsSendEmail';
import ActionsOutlookSendEmail from './ActionsOutlookSendEmail';
import ActionsOutlookMoveEmail from './ActionsOutlookMoveEmail';
import ActionsGmailSendEmail from './ActionsGmailSendEmail';
import ActionsLinkedinPost from './ActionsLinkedinPost';
import ActionsSendTeamsMessage from './ActionsSendTeamsMessage';
import ActionsCallMCP from './ActionsCallMCP';
import ActionsCreateTagBrowseWeb from './ActionsCreateTagBrowseWeb';
import ActionsBrowseWeb from './ActionsBrowseWeb'; // Import ActionsBrowseWeb
import ActionsComputerUse from './ActionsComputerUse';
import ActionsCMQVerifyDoctor from './ActionsCMQVerifyDoctor'; // Import ActionsCMQVerifyDoctor
import ActionsOIIQVerifyNurse from './ActionsOIIQVerifyNurse'; // Import ActionsOIIQVerifyNurse
import ActionsGenerativeAnswer from './ActionsGenerativeAnswer'; // Import ActionsGenerativeAnswer
import ActionsDeterministicAnswer from './ActionsDeterministicAnswer';
import ActionsFormExtraction from './ActionsFormExtraction';
import ActionsExtractImageToTag from './ActionsExtractImageToTag';
import ActionsSaveImageTagToFile from './ActionsSaveImageTagToFile';
import ActionsValidateReservation from './ActionsValidateReservation'; // Import ActionsValidateReservation
import ActionsValidateDoctorLicense from './ActionsValidateDoctorLicense'; // Import ActionsValidateDoctorLicense
import ActionsCropImage from './ActionsCropImage'; // Import ActionsCropImage
import ActionsSimpleJavascript from './ActionsSimpleJavascript'; // Import ActionsSimpleJavascript
import ActionsManualTag from './ActionsManualTag'; // Import ActionsManualTag
import ActionsDashboardSnapshot from './ActionsDashboardSnapshot';
import ActionsWeatherAPI from './ActionsWeatherAPI';
import ActionsTriggerFlow from './ActionsTriggerFlow';
import ActionsLoopTriggerFlow from './ActionsLoopTriggerFlow';
import ActionsPluginGeneric from './ActionsPluginGeneric.js';
import { ActionScopeProvider } from './ActionScopeContext';
import { useWebSocket } from '../WebSocketContext';
import NodeEditorDialogActions from '../ProjectDag/NodeEditorDialogActions';
import InlineNodeNameEditor from '../Common/InlineNodeNameEditor';

const SettingsDialog = ({
  open,
  onClose,
  setting, // This will be currentSettingForDialog
  settingType, // This will be selectedSettingType
  tags,
  caseid,
  onSaveStatusChange,
  onScopeChange, // Callback for scope changes
  onTargetExecutionChange,
  onActionCommand,
  isScopeChanging, // Loading state for scope toggle
  pluginActions = [],
  workflowFlows = [],
  onCreateWorkflowFlow,
  statusError = null,
  nodeEditorMode = false,
  onDelete = null,
  onRename = null,
  t, // For translations
}) => {
  const { subscribeToMessages } = useWebSocket() || {};
  const [saveError, setSaveError] = useState('');
  const [editorSaveDisabled, setEditorSaveDisabled] = useState(true);
  const [actionName, setActionName] = useState(setting?.name || '');
  const editorControlsRef = useRef(null);

  const registerEditorControls = useCallback((controls) => {
    editorControlsRef.current = controls;
    if (!controls) {
      return;
    }
    const disabled = Boolean(controls.saveDisabled);
    setEditorSaveDisabled((current) => current === disabled ? current : disabled);
  }, []);

  const handleEditorSave = useCallback(() => {
    editorControlsRef.current?.onSave?.();
  }, []);

  const handleHeaderNameSave = useCallback((nextName) => {
    const trimmedName = String(nextName || '').trim();
    if (!trimmedName) return;
    setActionName(trimmedName);
    if (typeof onRename === 'function' && setting?.id) {
      onRename(setting.id, trimmedName);
    }
  }, [onRename, setting?.id]);

  useEffect(() => {
    setSaveError('');
    editorControlsRef.current = null;
    setEditorSaveDisabled(true);
    setActionName(setting?.name || '');
  }, [setting?.id, setting?.name, settingType]);

  useEffect(() => {
    if (!open || !setting || typeof subscribeToMessages !== 'function') return undefined;
    return subscribeToMessages(['saveActionSettings'], (message) => {
      const payload = message?.data;
      if (!payload || typeof payload !== 'object') return;
      const responseId = payload.id ?? payload.action_id;
      const responseType = payload.type ?? payload.action_type;
      const idMatches = responseId !== undefined && responseId !== null
        && String(responseId) === String(setting.id);
      const typeMatches = responseType
        && String(responseType).toLowerCase() === String(settingType || '').toLowerCase();
      if (!idMatches && !typeMatches) return;

      const responseError = String(payload.error || payload.message || '').trim();
      if (payload.status === 'error' || payload.error) {
        setSaveError(responseError || t(
          'dag.action_update_error',
          'Could not save the action settings.'
        ));
        return;
      }
      setSaveError('');
    });
  }, [open, setting, settingType, subscribeToMessages, t]);

  if (!open || !setting) {
    return null; // Don't render if not open or no setting is selected
  }

  const dialogTitle = (actionName && actionName.trim() !== '')
    ? actionName
    : t('settings.edit_title', 'Edit Settings');

  const currentScope = setting.scope || 'event';
  const currentTargetExecutionMode = String(setting.target_execution_mode || 'sequential').toLowerCase() === 'parallel'
    ? 'parallel'
    : 'sequential';
  const currentMaxParallelTargets = Number.isInteger(Number(setting.max_parallel_targets))
    ? Math.min(Math.max(Number(setting.max_parallel_targets), 1), 25)
    : 5;
  const isNewSetting = typeof setting.id === 'string' && setting.id.startsWith('temp_');
  const pluginAction = Array.isArray(pluginActions)
    ? pluginActions.find((plugin) => plugin?.type === settingType)
    : null;
  const runtimeStatusErrorMessage = String(
    typeof statusError === 'string' ? statusError : statusError?.message || ''
  ).trim();
  const statusErrorMessage = saveError || runtimeStatusErrorMessage;
  const usesStandaloneNodeStatus = nodeEditorMode && Boolean(statusErrorMessage);

  const handleScopeChange = (event, newScope) => {
    if (newScope !== null && onScopeChange) {
      onScopeChange(setting.id, newScope);
    }
  };

  const handleTargetExecutionModeChange = (event, newMode) => {
    if (newMode !== null && onTargetExecutionChange) {
      onTargetExecutionChange(setting.id, newMode, currentMaxParallelTargets);
    }
  };

  const handleMaxParallelTargetsChange = (event) => {
    const parsedValue = Number(event.target.value);
    if (onTargetExecutionChange && Number.isInteger(parsedValue)) {
      onTargetExecutionChange(setting.id, currentTargetExecutionMode, parsedValue);
    }
  };

  return (
    <ActionScopeProvider
      scope={currentScope}
      caseid={caseid}
      setting={setting}
      sendActionCommand={onActionCommand}
      registerEditorControls={nodeEditorMode ? registerEditorControls : null}
      editorGeneralSettings={null}
    >
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 1, md: 1 },
            width: { xs: 'calc(100% - 16px)', md: 'calc(100% - 32px)' }
          }
        }}
        PaperProps={{ sx: { borderRadius: 2, overflowY: 'visible' } }}
      >      <DialogTitle
        data-testid={nodeEditorMode ? 'dag-action-dialog-title' : undefined}
        sx={{
        borderBottom: statusErrorMessage && !nodeEditorMode ? 'none' : '1px solid #e0e0e0',
        background: statusErrorMessage && !nodeEditorMode ? CONFIGURATION_ERROR_BACKGROUND : '#fff',
        color: statusErrorMessage && !nodeEditorMode ? '#fff' : 'inherit',
        pb: statusErrorMessage || nodeEditorMode ? 1.5 : 0,
        display: 'block',
        pr: 1
      }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          gap: 2,
        }}>
          <Box sx={{ minWidth: 0, flex: '1 1 220px', pt: 0.5 }}>
            {nodeEditorMode ? (
              <InlineNodeNameEditor
                value={dialogTitle}
                onSave={handleHeaderNameSave}
                t={t}
              />
            ) : (
              <Typography
                variant="h6"
                component="span"
                sx={{
                  fontWeight: 600,
                  whiteSpace: 'normal',
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                }}
              >
                {dialogTitle}
              </Typography>
            )}
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            flexWrap: 'wrap',
            flex: '0 1 auto',
            ml: 'auto',
            width: { xs: '100%', md: 'auto' },
          }}>
            {/* Scope Selector - Always visible */}
            <Tooltip
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                    {t('settings.scope_tooltip_title', 'Action Scope')}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.9)', lineHeight: 1.4 }}>
                    {currentScope === 'project'
                      ? t('settings.scope_project_desc', 'Run once when all events/files are processed')
                      : t('settings.scope_event_desc', 'Run for each event/file individually')}
                  </Typography>
                </Box>
              }
              placement="bottom"
              arrow
            >
              <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                {isScopeChanging && (
                  <CircularProgress
                    size={20}
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                      color: currentScope === 'project' ? '#c35a5f' : '#796dd9'
                    }}
                  />
                )}
                <ToggleButtonGroup
                  value={currentScope}
                  exclusive
                  onChange={handleScopeChange}
                  size="small"
                  disabled={isScopeChanging}
                  sx={{
                    height: 36,
                    backgroundColor: '#f5f5f7',
                    borderRadius: '20px',
                    padding: '3px',
                    border: 'none',
                    opacity: isScopeChanging ? 0.6 : 1,
                    transition: 'all 0.3s ease',
                    '& .MuiToggleButtonGroup-grouped': {
                      border: 'none',
                      borderRadius: '16px !important',
                      mx: 0.25,
                    },
                    '& .MuiToggleButton-root': {
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      color: '#666',
                      border: 'none',
                      borderRadius: '16px',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&.Mui-selected': {
                        background: currentScope === 'project'
                          ? 'linear-gradient(135deg, #c35a5f 0%, #e07070 100%)'
                          : 'linear-gradient(135deg, #796dd9 0%, #9a8ae8 100%)',
                        color: 'white',
                        boxShadow: currentScope === 'project'
                          ? '0 4px 12px rgba(195, 90, 95, 0.4)'
                          : '0 4px 12px rgba(121, 109, 217, 0.4)',
                        '&:hover': {
                          background: currentScope === 'project'
                            ? 'linear-gradient(135deg, #d67070 0%, #e88080 100%)'
                            : 'linear-gradient(135deg, #8a7ee3 0%, #a99bf0 100%)',
                        },
                      },
                      '&:hover:not(.Mui-selected)': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      },
                    },
                  }}
                >
                  <ToggleButton value="event" aria-label="event scope">
                    <DescriptionIcon sx={{ fontSize: 18, mr: 0.75 }} />
                    {t('settings.scope_event', 'Event')}
                  </ToggleButton>
                  <ToggleButton value="project" aria-label="project scope">
                    <FolderIcon sx={{ fontSize: 18, mr: 0.75 }} />
                    {t('settings.scope_project', 'Project')}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Tooltip>
            {currentScope === 'event' && (
              <Tooltip
                title={
                  <Box sx={{ p: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'white', mb: 0.5 }}>
                      {t('settings.target_execution_tooltip_title', 'Event execution')}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', color: 'rgba(255,255,255,0.9)', lineHeight: 1.4 }}>
                      {t(
                        'settings.target_execution_desc',
                        'Controls whether this action runs one event at a time or across multiple events in parallel.'
                      )}
                    </Typography>
                  </Box>
                }
                placement="bottom"
                arrow
              >
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1,
                  py: 0.5,
                  borderRadius: '20px',
                  backgroundColor: '#f5f5f7',
                }}>
                  <ToggleButtonGroup
                    value={currentTargetExecutionMode}
                    exclusive
                    onChange={handleTargetExecutionModeChange}
                    size="small"
                    sx={{
                      height: 32,
                      '& .MuiToggleButtonGroup-grouped': {
                        border: 'none',
                        borderRadius: '14px !important',
                        mx: 0.2,
                      },
                      '& .MuiToggleButton-root': {
                        px: 1.25,
                        py: 0.35,
                        fontSize: '0.76rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        color: '#666',
                        border: 'none',
                        borderRadius: '14px',
                        '&.Mui-selected': {
                          background: 'linear-gradient(135deg, #4f46a5 0%, #796dd9 100%)',
                          color: 'white',
                          boxShadow: '0 3px 10px rgba(87, 87, 176, 0.32)',
                        },
                      },
                    }}
                  >
                    <ToggleButton value="sequential" aria-label="sequential event execution">
                      {t('settings.target_execution_sequential', 'Sequential')}
                    </ToggleButton>
                    <ToggleButton value="parallel" aria-label="parallel event execution">
                      <BoltIcon sx={{ fontSize: 16, mr: 0.5 }} />
                      {t('settings.target_execution_parallel', 'Parallel events')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                  {currentTargetExecutionMode === 'parallel' && (
                    <TextField
                      type="number"
                      size="small"
                      label={t('settings.max_parallel_targets', 'Max')}
                      value={currentMaxParallelTargets}
                      onChange={handleMaxParallelTargetsChange}
                      inputProps={{ min: 1, max: 25, step: 1 }}
                      sx={{
                        width: 82,
                        '& .MuiInputBase-root': {
                          height: 32,
                          borderRadius: '14px',
                          backgroundColor: '#ffffff',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.72rem',
                        },
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            )}
          <CloseButton
            ariaLabel="close"
            onClick={onClose}
            sx={{
              color: statusErrorMessage && !nodeEditorMode ? '#fff' : (theme) => theme.palette.grey[500],
            }}
          />
          </Box>
        </Box>
        {!nodeEditorMode && (
          <ConfigurationErrorSummary error={statusErrorMessage} t={t} />
        )}
        {usesStandaloneNodeStatus && (
          <NodeEditorStatusAlert
            message={statusErrorMessage}
            testId="dag-action-status"
            sx={{ mt: 1.5, mb: 0 }}
          />
        )}
        </DialogTitle>
        <DialogContent sx={{ pt: 2, px: { xs: 1, sm: 2 }, pb: 2, overflowY: 'auto' }}>
          {settingType === 'createfile' && (
          <ActionsCreateFile
            tags={tags}
            settingId={setting.id}
            caseid={caseid}
            onSaveStatusChange={onSaveStatusChange}
            initialScope={currentScope}
          />
        )}
          {settingType === 'addcsvcolumn' && (
            <ActionsAddCsvColumn
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'addexcelcolumn' && (
            <ActionsAddExcelColumn
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'addexcelrow' && (
            <ActionsAddExcelRow
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'copyfile' && (
            <ActionsCopyFile
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createfolder' && (
            <ActionsCreateFolder
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createproject' && (
            <ActionsCreateProject
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'generatewidget' && (
            <ActionsGenerateWidget
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createdocx' && (
            <ActionsCreateDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createdocxtemplate' && (
            <ActionsCreateDocxTemplate
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createdocxtemplatev2' && (
            <ActionsCreateDocxTemplateV2
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'replacewordtext' && (
            <ActionsReplaceWordText
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'anonymizepii' && (
            <ActionsAnonymizePII
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createexcelfromtemplate' && (
            <ActionsCreateExcelFromTemplate
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'addpassworddocx' && (
            <ActionsAddPasswordDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'addpasswordexcel' && (
            <ActionsAddPasswordExcel
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'convertdocxtopdf' && (
            <ActionsConvertDocxToPdf
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'convertpdftodocx' && (
            <ActionsConvertPdfToDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'documentlegalpdftodocx' && (
            <ActionsConvertPdfToDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="documentlegalpdftodocx"
              titleOverride={t('workflow.documentlegalpdftodocx', 'Legal Document - PDF to DOCX')}
            />
          )}
          {settingType === 'convertpdftohtml' && (
            <ActionsConvertPdfToDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="convertpdftohtml"
              titleOverride={t('workflow.convertpdftohtml', 'Convert PDF/Image to HTML')}
            />
          )}
          {settingType === 'convertpdfimagetopdftext' && (
            <ActionsConvertPdfToDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="convertpdfimagetopdftext"
              titleOverride={t('workflow.convertpdfimagetopdftext', 'Convert PDF/Image to Text PDF')}
            />
          )}
          {settingType === 'rotatepdfimageocrpdf' && (
            <ActionsConvertPdfToDocx
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="rotatepdfimageocrpdf"
              titleOverride={t('workflow.rotatepdfimageocrpdf', 'Straighten the Page')}
            />
          )}
          {settingType === 'pdftotoword' && (
            <ActionsPdfTextToWord
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createpdf' && (
            <ActionsCreatePdf
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'insertimagetopdf' && (
            <ActionsInsertImageIntoPdf
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'searchandinsertimagetopdf' && (
            <ActionsSearchAndInsertImageIntoPdf
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'searchandinsertrichtexttopdf' && (
            <ActionsSearchAndInsertRichTextIntoPdf
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createzipfile' && (
            <ActionsCreateZipFile
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createjsonfile' && (
            <ActionsCreateJsonFile
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createxmlfile' && (
            <ActionsCreateXmlFile
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'sendapi' && (
            <ActionsSendAPI
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'sendemail' && (
            <ActionsSendEmail
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'sendOutlookEmail' && (
            <ActionsOutlookSendEmail
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'moveOutlookEmail' && (
            <ActionsOutlookMoveEmail
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'sendGmailEmail' && (
            <ActionsGmailSendEmail
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'sendLinkedinPost' && (
            <ActionsLinkedinPost
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'sendLinkedinCompanyPost' && (
            <ActionsLinkedinPost
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="sendLinkedinCompanyPost"
              actionTitleKey="workflow.send_linkedin_company_post.title"
              actionTitleFallback="Publish post (LinkedIn Company)"
              i18nBaseKey="workflow.linkedin_company_post"
              isCompanyPost
            />
          )}
          {settingType === 'msteamsSendMessage' && (
            <ActionsSendTeamsMessage
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'callmcp' && (
            <ActionsCallMCP
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'createtagbrowseweb' && (
            <ActionsCreateTagBrowseWeb
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}        {settingType === 'browseweb' && (
            <ActionsBrowseWeb
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'computeruse' && (
            <ActionsComputerUse
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'cmqverifydoctor' && (
            <ActionsCMQVerifyDoctor
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'oiiqverifynurse' && (
            <ActionsOIIQVerifyNurse
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'generativeanswer' && (
            <ActionsGenerativeAnswer
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'generativeanswerfile' && (
            <ActionsGenerativeAnswer
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="generativeanswerfile"
              actionTitle={t('workflow.generativeanswerfile', 'File Generative Answer')}
            />
          )}
          {settingType === 'deterministicanswer' && (
            <ActionsDeterministicAnswer
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              actionType="deterministicanswer"
              actionTitle={t('deterministicanswer', 'Deterministic Answer')}
              defaultOutputTag="deterministic_result"
              defaultOutputDescription={t('deterministicanswer.result_description', 'Deterministic answer result')}
            />
          )}
          {settingType === 'formextraction' && (
            <ActionsFormExtraction
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'extractimagetotag' && (
            <ActionsExtractImageToTag
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'saveimagetofile' && (
            <ActionsSaveImageTagToFile
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'validatereservation' && (
            <ActionsValidateReservation
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'validatedoctorlicense' && (
            <ActionsValidateDoctorLicense
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'cropimage' && (
            <ActionsCropImage
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'simplejavascript' && (
            <ActionsSimpleJavascript
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'manualtag' && (
            <ActionsManualTag
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'triggerflow' && (
            <ActionsTriggerFlow
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              workflowFlows={workflowFlows}
              onCreateWorkflowFlow={onCreateWorkflowFlow}
              currentFlowId={setting.flow_id}
              initialScope={currentScope}
            />
          )}
          {settingType === 'looptriggerflow' && (
            <ActionsLoopTriggerFlow
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              workflowFlows={workflowFlows}
              currentFlowId={setting.flow_id}
              initialScope={currentScope}
            />
          )}
          {settingType === 'dashboardsnapshot' && (
            <ActionsDashboardSnapshot
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {settingType === 'weatherapi' && (
            <ActionsWeatherAPI
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
            />
          )}
          {pluginAction && (
            <ActionsPluginGeneric
              tags={tags}
              settingId={setting.id}
              caseid={caseid}
              onSaveStatusChange={onSaveStatusChange}
              initialScope={currentScope}
              pluginAction={pluginAction}
            />
          )}
        </DialogContent>
        {nodeEditorMode ? (
          <NodeEditorDialogActions
            onDelete={typeof onDelete === 'function' ? () => onDelete(setting) : null}
            onCancel={onClose}
            onSave={handleEditorSave}
            deleteDisabled={isNewSetting}
            saveDisabled={editorSaveDisabled}
          />
        ) : (
          <DialogActions sx={{ borderTop: '1px solid #e0e0e0', pt: 1.5, pb: 1.5, pr: 2, pl: 2 }} />
        )}
      </Dialog>
    </ActionScopeProvider>
  );
};

export default SettingsDialog;
